// components/public/SavedUnitsDrawer.tsx
/**
 * @description Drawer that slides up showing saved units
 * Triggered from sticky footer badge
 */
import { X, ChevronRight, Mail, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSavedUnits } from '../../context/SavedUnitsContext';
import { formatPrice, formatAvailability } from '../../lib/utils';
import { PLACEHOLDER_IMAGES, MOCK_BUILDING } from '../../lib/mockData';

interface SavedUnitsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SavedUnitsDrawer({ isOpen, onClose }: SavedUnitsDrawerProps) {
  const { savedUnits, removeUnit, clearAll } = useSavedUnits();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleCompare = () => {
    onClose();
    navigate('/compare');
  };

  const handleContactAll = () => {
    const unitNumbers = savedUnits.map(u => u.unit_number).join(', ');
    window.location.href = `mailto:${MOCK_BUILDING.leasing_email}?subject=Inquiry about Units ${unitNumbers} at ${MOCK_BUILDING.name}`;
  };

  const handleViewUnit = (unitId: string) => {
    onClose();
    navigate(`/b/${MOCK_BUILDING.slug}/unit/${unitId}`);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[100] animate-fade-in"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[101] max-w-[430px] mx-auto animate-slide-up max-h-[70vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            Saved Units ({savedUnits.length})
          </h2>
          <div className="flex items-center gap-2">
            {savedUnits.length > 0 && (
              <button
                onClick={clearAll}
                className="text-sm text-gray-500 hover:text-red-500 flex items-center gap-1"
              >
                <Trash2 size={14} />
                Clear
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Units List */}
        <div className="flex-1 overflow-y-auto p-4">
          {savedUnits.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No units saved yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Tap the heart icon on any unit to save it
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {savedUnits.map((unit) => (
                <div
                  key={unit.id}
                  className="flex bg-white border border-gray-200 rounded-xl overflow-hidden"
                >
                  {/* Thumbnail */}
                  <div className="w-24 h-24 flex-shrink-0">
                    <img
                      src={PLACEHOLDER_IMAGES.livingRoom}
                      alt={unit.unit_type?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 p-3 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start">
                        <h3 className="font-semibold text-gray-900">
                          {unit.unit_type?.name}
                        </h3>
                        <button
                          onClick={() => removeUnit(unit.id)}
                          className="p-1 hover:bg-gray-100 rounded-full -mr-1 -mt-1"
                        >
                          <X size={16} className="text-gray-400" />
                        </button>
                      </div>
                      <p className="text-primary font-bold text-sm">
                        {formatPrice(unit.price)}/mo
                      </p>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">
                        Floor {unit.floor} • {formatAvailability(unit.availability_date)}
                      </span>
                      <button
                        onClick={() => handleViewUnit(unit.id)}
                        className="text-xs text-primary font-medium flex items-center"
                      >
                        View <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {savedUnits.length > 0 && (
          <div className="flex gap-3 p-4 border-t border-gray-100 flex-shrink-0 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <button
              onClick={handleCompare}
              className="flex-1 py-3 border border-primary text-primary rounded-lg font-medium text-sm hover:bg-primary/5 transition-colors"
            >
              Compare Details
            </button>
            <button
              onClick={handleContactAll}
              className="flex-1 py-3 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
            >
              <Mail size={16} />
              Contact About All
            </button>
          </div>
        )}
      </div>
    </>
  );
}
